import React, { useMemo, useState } from 'react'
import { Clock, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'

function parseHm(hm: string | null | undefined): { h: number; m: number } | null {
  if (!hm) return null
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(hm.trim())
  if (!match) return null
  const h = Number(match[1])
  const m = Number(match[2])
  if (h < 0 || h > 23 || m < 0 || m > 59) return null
  return { h, m }
}

function formatHm(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

type Props = {
  id?: string
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  clearable?: boolean
  /** Minute step in the picker (default 5). */
  minuteIncrement?: number
  'aria-label'?: string
}

export function TimePicker({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  clearable = true,
  minuteIncrement = 5,
  'aria-label': ariaLabel,
}: Props) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const parsed = parseHm(value)
  const resolvedPlaceholder = placeholder ?? t('hr.checkIn')

  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), [])
  const minutes = useMemo(() => {
    const step = Math.max(1, Math.min(30, minuteIncrement))
    const list: number[] = []
    for (let m = 0; m < 60; m += step) list.push(m)
    if (parsed && !list.includes(parsed.m)) {
      list.push(parsed.m)
      list.sort((a, b) => a - b)
    }
    return list
  }, [minuteIncrement, parsed])

  const selectedH = parsed?.h ?? 9
  const selectedM = parsed?.m ?? 0

  const commit = (h: number, m: number) => {
    onChange(formatHm(h, m))
  }

  return (
    <div className={cn('inline-flex items-center gap-1', className)} dir={i18n.dir()}>
      <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            aria-label={ariaLabel ?? resolvedPlaceholder}
            className={cn(
              'h-9 min-w-[7.5rem] justify-start gap-2 px-2.5 font-normal tabular-nums',
              !value && 'text-muted-foreground',
            )}
          >
            <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span dir="ltr" className="tabular-nums">
              {value ? value.slice(0, 5) : resolvedPlaceholder}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="mb-2 text-xs font-medium text-muted-foreground">{resolvedPlaceholder}</div>
          <div className="flex gap-2" dir="ltr">
            <div className="flex max-h-48 w-14 flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-md border border-border-subtle p-1">
              <div className="px-1 pb-1 text-center text-[10px] font-medium text-muted-foreground">
                {t('hr.timeHour')}
              </div>
              {hours.map((h) => (
                <button
                  key={h}
                  type="button"
                  className={cn(
                    'rounded-md px-2 py-1.5 text-center text-sm tabular-nums transition-colors',
                    selectedH === h && value
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted',
                  )}
                  onClick={() => commit(h, selectedM)}
                >
                  {String(h).padStart(2, '0')}
                </button>
              ))}
            </div>
            <div className="flex items-center text-sm font-medium text-muted-foreground" aria-hidden>
              :
            </div>
            <div className="flex max-h-48 w-14 flex-col gap-0.5 overflow-y-auto overscroll-contain rounded-md border border-border-subtle p-1">
              <div className="px-1 pb-1 text-center text-[10px] font-medium text-muted-foreground">
                {t('hr.timeMinute')}
              </div>
              {minutes.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={cn(
                    'rounded-md px-2 py-1.5 text-center text-sm tabular-nums transition-colors',
                    selectedM === m && value
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted',
                  )}
                  onClick={() => {
                    commit(selectedH, m)
                    setOpen(false)
                  }}
                >
                  {String(m).padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {clearable && value && !disabled ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-muted-foreground"
          onClick={() => onChange(null)}
          aria-label={t('pos.datePicker.clear')}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  )
}
